import { ChangeDetectionStrategy, Component, Input, OnChanges } from '@angular/core';

interface SeatPad {
  /** Centre of the seat pad. */
  x: number;
  y: number;
  /** Degrees; the pad's long edge runs along the table edge it faces. */
  angle: number;
}

/**
 * Top-down table drawing: the table body with seat pads spaced around it and
 * the table name in the middle. Pure SVG so it scales to any cell size and
 * rotates cleanly, unlike the canvas preview on the add-table page whose
 * geometry it borrows.
 *
 * Status colours follow the list view's pills: green when available, brand
 * danger when occupied, brand outline otherwise.
 */
@Component({
  selector: 'app-table-glyph',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <svg
      class="tg"
      [class.tg--available]="status === 'available'"
      [class.tg--occupied]="status === 'occupied'"
      [class.tg--reserved]="status === 'reserved'"
      [class.tg--selected]="selected"
      [class.tg--dimmed]="dimmed"
      [attr.viewBox]="'0 0 ' + width + ' ' + height"
      [attr.width]="width"
      [attr.height]="height"
      role="img"
      [attr.aria-label]="name + ', ' + seats + ' seats, ' + status"
    >
      @for (seat of seatPads; track $index) {
        <rect
          class="tg-seat"
          [attr.x]="seat.x - seatLength / 2"
          [attr.y]="seat.y - seatDepth / 2"
          [attr.width]="seatLength"
          [attr.height]="seatDepth"
          [attr.rx]="seatDepth / 2.5"
          [attr.transform]="'rotate(' + seat.angle + ' ' + seat.x + ' ' + seat.y + ')'"
        />
      }
      @if (round) {
        <ellipse
          class="tg-body"
          [attr.cx]="width / 2"
          [attr.cy]="height / 2"
          [attr.rx]="bodyW / 2"
          [attr.ry]="bodyH / 2"
        />
      } @else {
        <rect
          class="tg-body"
          [attr.x]="(width - bodyW) / 2"
          [attr.y]="(height - bodyH) / 2"
          [attr.width]="bodyW"
          [attr.height]="bodyH"
          [attr.rx]="cornerRadius"
        />
      }
      <text
        class="tg-name"
        [attr.x]="width / 2"
        [attr.y]="height / 2 + (showSeats ? -fontSize * 0.15 : fontSize * 0.35)"
        [attr.font-size]="fontSize"
        text-anchor="middle"
      >
        {{ name }}
      </text>
      @if (showSeats) {
        <text
          class="tg-seats"
          [attr.x]="width / 2"
          [attr.y]="height / 2 + fontSize * 0.95"
          [attr.font-size]="fontSize * 0.7"
          text-anchor="middle"
        >
          {{ seats }} seats
        </text>
      }
    </svg>
  `,
  styles: [
    `
      :host {
        display: block;
        line-height: 0;
      }
      .tg {
        display: block;
        overflow: visible;
        transition: opacity 0.15s ease;
      }
      .tg--dimmed {
        opacity: 0.25;
      }
      .tg-body {
        fill: var(--c-surface);
        stroke: var(--c-brand);
        stroke-width: 2;
      }
      .tg-seat {
        fill: var(--c-surface-2);
        stroke: var(--c-brand);
        stroke-width: 1.5;
      }
      .tg--available .tg-body {
        fill: #e2f4e6;
        stroke: #2f7d45;
      }
      .tg--available .tg-seat {
        stroke: #2f7d45;
      }
      .tg--occupied .tg-body {
        fill: var(--c-danger-tint-2);
        stroke: var(--c-danger-strong);
      }
      .tg--occupied .tg-seat {
        fill: var(--c-danger-tint-2);
        stroke: var(--c-danger-strong);
      }
      .tg--reserved .tg-body {
        fill: #fef4eb;
        stroke: #b45309;
      }
      .tg--reserved .tg-seat {
        stroke: #b45309;
      }
      .tg--selected .tg-body {
        stroke: var(--c-brand);
        stroke-width: 3;
        filter: drop-shadow(0 0 4px rgb(var(--c-brand-rgb, 179 29 44) / 0.55));
      }
      .tg-name {
        fill: var(--c-text);
        font-weight: var(--fw-semibold, 600);
        pointer-events: none;
        user-select: none;
      }
      .tg-seats {
        fill: var(--c-text-2);
        pointer-events: none;
        user-select: none;
      }
      :host-context(body.app-theme-dark) .tg--available .tg-body {
        fill: rgba(108, 212, 140, 0.12);
        stroke: #6cd48c;
      }
      :host-context(body.app-theme-dark) .tg--available .tg-seat {
        stroke: #6cd48c;
      }
      :host-context(body.app-theme-dark) .tg--occupied .tg-body,
      :host-context(body.app-theme-dark) .tg--occupied .tg-seat {
        fill: rgb(var(--c-danger-rgb, 235 71 71) / 0.15);
        stroke: var(--c-danger);
      }
    `,
  ],
})
export class TableGlyphComponent implements OnChanges {
  @Input() name = '';
  @Input() seats = 0;
  @Input() shape = 'square';
  @Input() status = '';
  /** Pixel box the glyph must fit, seats included. */
  @Input() width = 80;
  @Input() height = 80;
  @Input() selected = false;
  @Input() dimmed = false;

  round = false;
  bodyW = 0;
  bodyH = 0;
  seatLength = 0;
  seatDepth = 0;
  cornerRadius = 0;
  fontSize = 12;
  showSeats = false;
  seatPads: SeatPad[] = [];

  ngOnChanges(): void {
    this.recompute();
  }

  private recompute(): void {
    const w = Math.max(8, this.width);
    const h = Math.max(8, this.height);
    const minSide = Math.min(w, h);
    this.round = ['circle', 'oval'].includes((this.shape || '').toLowerCase());

    // Seats sit outside the body, so the body is inset by a seat's depth
    // plus a small gap on every side.
    this.seatDepth = Math.min(14, Math.max(5, minSide * 0.12));
    this.seatLength = Math.min(24, Math.max(8, minSide * 0.24));
    const gap = Math.max(2, this.seatDepth * 0.35);
    const inset = this.seatDepth + gap;
    this.bodyW = Math.max(6, w - inset * 2);
    this.bodyH = Math.max(6, h - inset * 2);
    this.cornerRadius = Math.min(14, this.bodyW * 0.15, this.bodyH * 0.15);
    this.fontSize = Math.min(16, Math.max(9, Math.min(this.bodyW, this.bodyH) * 0.24));
    this.showSeats = this.bodyH > this.fontSize * 2.6 && this.bodyW > this.fontSize * 3.5;

    const seats = Math.max(0, Math.round(this.seats || 0));
    const cx = w / 2;
    const cy = h / 2;
    const offset = gap + this.seatDepth / 2;
    this.seatPads = this.round
      ? this.seatsOnEllipse(cx, cy, this.bodyW / 2 + offset, this.bodyH / 2 + offset, seats)
      : this.seatsOnRect(cx, cy, this.bodyW, this.bodyH, offset, seats);
  }

  /** Evenly spaced around the ellipse, starting from the top and going clockwise. */
  private seatsOnEllipse(cx: number, cy: number, rx: number, ry: number, n: number): SeatPad[] {
    const pads: SeatPad[] = [];
    for (let i = 0; i < n; i++) {
      const t = -Math.PI / 2 + (i * 2 * Math.PI) / n;
      const x = cx + rx * Math.cos(t);
      const y = cy + ry * Math.sin(t);
      // Pad lies tangent to the ellipse at that point.
      const angle = (Math.atan2(ry * Math.cos(t), -rx * Math.sin(t)) * 180) / Math.PI;
      pads.push({ x, y, angle });
    }
    return pads;
  }

  /**
   * Seats are dealt to the four edges in proportion to their length, then
   * spread evenly along each edge. Long tables therefore get most seats on
   * the long sides, matching how they are laid in a dining room.
   */
  private seatsOnRect(
    cx: number,
    cy: number,
    bodyW: number,
    bodyH: number,
    offset: number,
    n: number,
  ): SeatPad[] {
    const counts = [0, 0, 0, 0]; // top, right, bottom, left
    const lengths = [bodyW, bodyH, bodyW, bodyH];
    const minSpacing = this.seatLength * 1.15;
    // Visit opposite edges consecutively so ties (a square) fill top then
    // bottom, and a 2-seater reads as a facing pair instead of a corner.
    const order = [0, 2, 1, 3];
    for (let i = 0; i < n; i++) {
      // Give the next seat to the edge with the most room per seat.
      let best = 0;
      let bestRoom = -1;
      for (const side of order) {
        const room = lengths[side] / (counts[side] + 1);
        if (room > bestRoom + 0.001) {
          bestRoom = room;
          best = side;
        }
      }
      counts[best]++;
    }

    const pads: SeatPad[] = [];
    const left = cx - bodyW / 2;
    const top = cy - bodyH / 2;
    const along = (count: number, length: number, index: number) => {
      const spacing = Math.max(minSpacing, length / count);
      const total = spacing * (count - 1);
      return -total / 2 + index * spacing;
    };
    for (let i = 0; i < counts[0]; i++) {
      pads.push({ x: cx + along(counts[0], bodyW, i), y: top - offset, angle: 0 });
    }
    for (let i = 0; i < counts[2]; i++) {
      pads.push({ x: cx + along(counts[2], bodyW, i), y: top + bodyH + offset, angle: 0 });
    }
    for (let i = 0; i < counts[1]; i++) {
      pads.push({ x: left + bodyW + offset, y: cy + along(counts[1], bodyH, i), angle: 90 });
    }
    for (let i = 0; i < counts[3]; i++) {
      pads.push({ x: left - offset, y: cy + along(counts[3], bodyH, i), angle: 90 });
    }
    return pads;
  }
}
